import { usePanel } from "@/context/panel-context";
import { useProject } from "@/context/current-project-context";
import { AGENTS, AGENTS_VALUES, MENU_ITEMS } from "@/lib/constants";
import useGetProjectById from "@/service-hooks/use-get-project-by-id-service";
import { isUuid } from "@/lib/uuid";
import { useGetCodeOntologyName } from "@/service-hooks/use-code-ontology-service";
import {
  useNavigate,
  useParams,
  useLocation,
} from "react-router-dom";
import {
  ChevronRight,
  Palette,
  Home,
  Bot,
  BarChart3,
  Layers,
  ChevronDown,
  MenuIcon,
  Code,
  Zap,
  Settings,
  Network,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

import { useEffect, useRef, useState } from "react";
import { useDiagram } from "@/context/diagram-context";
import { exportAsPNG, exportAsSVG } from "@/lib/export-utils";
import { useHistory } from "@/context/history-context";
import SaveComponentDialog from "@/components/panels/save-component-dialog";
import SaveNewDiagram from "@/components/layout/save-new-diagram";
import DownloadDiagramDialog from "@/components/layout/download-diagram-dialog";
import SettingsDialog from "@/components/layout/settings-dialog";
import { useSaveComponentToLibrary } from "@/hooks/use-save-component-to-library";
import useSaveDiagram from "@/service-hooks/use-save-diagram-service";
import { generateDiagramSVGContent } from "@/lib/svg-utils.lib";
import * as diagramComponentsLib from "@/lib/diagram-components.lib";
import { componentLibraryManager } from "@/lib/component.lib";
import { shapesLibraryManager } from "@/lib/shapes-manager";
import { useLibrary } from "@/context/library-context";
import { useUI } from "@/context/ui-context";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

// Function to save file locally
const saveLocalFile = async (
  fileName: string,
  content: string,
): Promise<void> => {
  const blob = new Blob([content], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  try {
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
  } finally {
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};

// Function to get JSON filename with extension
const getJsonFileName = (fileName: string): string => {
  return fileName.endsWith(".json") ? fileName : `${fileName}.json`;
};

// Function to load file from local file system
const loadFile = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      if (event.target?.result) {
        resolve(event.target.result as string);
      } else {
        reject(new Error("Failed to read file"));
      }
    };
    reader.onerror = () => reject(new Error("Error reading file"));
    reader.readAsText(file);
  });
};

// Custom hooks that safely handle missing contexts
const useSafeDiagram = () => {
  try {
    return useDiagram();
  } catch {
    return null;
  }
};

const useSafeHistory = () => {
  try {
    return useHistory();
  } catch {
    return null;
  }
};

const useSafeLibrary = () => {
  try {
    return useLibrary();
  } catch {
    return null;
  }
};

const useSafeUI = () => {
  try {
    return useUI();
  } catch {
    // Return a minimal UI context object with default values
    return {
      isDropdownOpen: false,
      setIsDropdownOpen: () => {},
      isSaveComponentDialogOpen: false,
      openSaveComponentDialog: () => {},
      closeSaveComponentDialog: () => {},
      isSaveDiagramDialogOpen: false,
      openSaveDiagramDialog: () => {},
      closeSaveDiagramDialog: () => {},
      isSettingsDialogOpen: false,
      openSettingsDialog: () => {},
      closeSettingsDialog: () => {},
      showAttachmentPoints: false,
      setShowAttachmentPoints: () => {},
      canvasSettings: {
        showAttachmentPoints: false,
        backgroundColor: "#020C19",
        gridEnabled: true,
        snapToGrid: true,
        gridSize: 20,
      },
      setCanvasSettings: () => {},
    };
  }
};

export default function BreadCrumb() {
  const { agent = "Agents", id = "" } = useParams();
  const { panelType, setPanelType } = usePanel();
  const { setCurrentProject } = useProject();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  const diagram = useSafeDiagram();
  const history = useSafeHistory();
  const library = useSafeLibrary();
  const ui = useSafeUI();
  const { data, error, isFetched } = useGetProjectById(id, !!id);

  // Update currentProject context when project data is fetched
  useEffect(() => {
    if (data && data._id) {
      setCurrentProject(data);
    }
  }, [data, setCurrentProject]);

  // A wrong or no-longer-shared project link goes to the projects list straight away, which then shows a banner
  // that stays until dismissed (BREEZEAI-1471 BUG-11). A malformed id is not even fetched.
  useEffect(() => {
    if (!id) return;
    const malformed = !isUuid(id);
    if (!malformed && !isFetched) return;
    const projectNotFound = malformed || !data?._id || !!error;
    if (!projectNotFound) return;
    navigate("/", {
      replace: true,
      state: { projectNotice: "This project doesn't exist, or you don't have access to it. Ask a project owner to share it with you." },
    });
  }, [id, isFetched, data?._id, error, navigate]);
  // Extract library functions with null safety
  const handleMissingDependencies = library?.handleMissingDependencies;
  const setSvgLibrary = library?.setSvgLibrary;
  const setComponents = library?.setComponents;
  // Extract UI state (useSafeUI now returns default values instead of null)
  const {
    isDropdownOpen,
    setIsDropdownOpen,
    isSaveComponentDialogOpen: isSaveDialogOpen,
    openSaveComponentDialog: openSaveDialog,
    closeSaveComponentDialog: closeSaveDialog,
    isSaveDiagramDialogOpen,
    openSaveDiagramDialog,
    closeSaveDiagramDialog,
    isSettingsDialogOpen,
    openSettingsDialog,
    closeSettingsDialog,
  } = ui;

  const [isDownloadDiagramDialogOpen, setIsDownloadDiagramDialogOpen] =
    useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Use the custom hook for saving components
  const { saveComponent, isSuccess } = useSaveComponentToLibrary();
  const [saveDiagramError, setSaveDiagramError] = useState<string | null>(null);

  // Save diagram mutation
  const saveDiagramMutation = useSaveDiagram((res, error) => {
    if (error) {
      // Handle duplicate name error (409 status)
      const errorData = error as any;
      if (errorData?.status === 409) {
        setSaveDiagramError(
          "A diagram with this name already exists. Please choose a different name.",
        );
      } else {
        setSaveDiagramError(
          errorData?.message || "Failed to save diagram. Please try again.",
        );
      }
    } else if (res?._id) {
      queryClient.invalidateQueries({
        queryKey: ["diagrams-uuid"],
      });
      setSaveDiagramError(null);
      closeSaveDiagramDialog();
    }
  });

  // Close dialog on successful save
  if (isSuccess && isSaveDialogOpen) {
    closeSaveDialog();
  }

  // Determine current page context
  const isDashboard = location.pathname.startsWith("/dashboard");
  const isAgents = location.pathname.startsWith("/agents");
  const isChat = location.pathname.startsWith("/chat");
  // Trailing slash matters: without it this also matches /knowledge-graph/:id
  // and the graph page renders under the Artifacts breadcrumb.
  const isKnowledge = location.pathname.startsWith("/knowledge/");
  const isKnowledgeGraph = location.pathname.startsWith("/knowledge-graph/");
  const isProjects = location.pathname === "/";
  const isCodeOntologyList = location.pathname.match(
    /^\/code-ontology\/[^/]+$/,
  );
  const isCodeOntologyGraph =
    location.pathname.includes("/code-ontology/") &&
    location.pathname.includes("/graph/");
  const isCodeOntologyNodes =
    location.pathname.includes("/code-ontology/") &&
    location.pathname.includes("/nodes/");

  // Pull ontologyId from /code-ontology/:id/(graph|nodes)/:ontologyId so we
  // can label the last crumb with the actual repository (BREEZEAI-953).
  // The route param `id` is already the project uuid — using it directly
  // avoids blocking on the async project-metadata fetch.
  const codeOntologyId = (isCodeOntologyGraph || isCodeOntologyNodes)
    ? location.pathname.split("/").pop() || ""
    : "";
  const { data: codeOntologyMeta } = useGetCodeOntologyName(
    codeOntologyId,
    id,
    !!codeOntologyId && !!id,
  );
  // Prefer the real repository name. If missing (older ontologies were saved
  // without it), derive from repoUrl (e.g. github.com/foo/BAR.git → BAR) or
  // repositoryPath (last path segment). Only fall back to the user-typed
  // ontology name as a last resort — that name is often "test" / "testing"
  // and doesn't tell the user which repo they're looking at.
  const deriveRepoName = (url?: string) => {
    if (!url) return "";
    const trimmed = url.replace(/\.git$/i, "").replace(/\/+$/, "");
    const segments = trimmed.split(/[\/\\]/).filter(Boolean);
    return segments[segments.length - 1] || "";
  };
  const codeOntologyLabel =
    codeOntologyMeta?.repositoryName ||
    deriveRepoName(codeOntologyMeta?.repoUrl) ||
    deriveRepoName(codeOntologyMeta?.repositoryPath) ||
    codeOntologyMeta?.name ||
    "";
  const isFunctionalOntology = location.pathname.startsWith("/functional-ontology");
  const isOntologyManager = location.pathname.startsWith("/ontology");
  const isSettings = location.pathname.startsWith("/settings");

  // Only show diagram menu on pages that support it
  const showDiagramMenu = isChat && agent === "architecture_agent";

  const breadcrumbItems = [];

  // Add project-level breadcrumb for non-root pages
  if (!isProjects) {
    breadcrumbItems.push({
      label: data?.name || "Project",
      icon: Home,
      onClick: () => navigate("/"),
      isActive: false,
    });
  }

  // Add second level based on current route
  if (isProjects) {
    breadcrumbItems.push({
      label: "Projects",
      icon: Layers,
      onClick: () => navigate("/"),
      isActive: true,
    });
  } else if (isDashboard || isAgents) {
    breadcrumbItems.push({
      label: "Dashboard",
      icon: BarChart3,
      onClick: () => {
        navigate(`/dashboard/${id}`);
        if (panelType) {
          setPanelType(null);
        }
      },
      isActive: true,
    });
  } else if (isKnowledgeGraph) {
    breadcrumbItems.push({
      label: "Ontology Visualization",
      icon: Network,
      onClick: () => navigate(`/knowledge-graph/${id}`),
      isActive: true,
    });
  } else if (isKnowledge) {
    breadcrumbItems.push({
      label: "Artifacts",
      icon: Zap,
      onClick: () => navigate(`/knowledge/${id}`),
      isActive: true,
    });
  } else if (isChat) {
    breadcrumbItems.push({
      label: "Dashboard",
      icon: BarChart3,
      onClick: () => {
        navigate(`/dashboard/${id}`);
        if (panelType) {
          setPanelType(null);
        }
      },
      isActive: false,
    });
    breadcrumbItems.push({
      label: AGENTS_VALUES[agent as AGENTS] || "Agent",
      icon: Bot,
      onClick: () => {},
      isActive: true,
    });
  } else if (isCodeOntologyList || isCodeOntologyGraph || isCodeOntologyNodes) {
    breadcrumbItems.push({
      label: "Dashboard",
      icon: BarChart3,
      onClick: () => {
        navigate(`/dashboard/${id}`);
        if (panelType) {
          setPanelType(null);
        }
      },
      isActive: false,
    });
    breadcrumbItems.push({
      label: "Code Ontology",
      icon: Code,
      onClick: () => navigate(`/code-ontology/${id}`),
      isActive: !!isCodeOntologyList,
    });

    // Add repository-scoped crumb — shows the exact repo name (or "Loading…"
    // until the query resolves) and then the view label. Falls back to the
    // ontologyId if the metadata query fails so users always have context.
    if (isCodeOntologyGraph || isCodeOntologyNodes) {
      breadcrumbItems.push({
        label: codeOntologyLabel || (codeOntologyId ? `#${codeOntologyId}` : "Repository"),
        icon: Code,
        onClick: () => navigate(`/code-ontology/${id}`),
        isActive: false,
      });
      breadcrumbItems.push({
        label: isCodeOntologyGraph ? "Graph" : "Nodes",
        icon: BarChart3,
        onClick: () => {},
        isActive: true,
      });
    }
  } else if (isOntologyManager) {
  } else if (isSettings) {
    breadcrumbItems.push({
      label: "Dashboard",
      icon: BarChart3,
      onClick: () => {
        navigate(`/dashboard/${id}`);
        if (panelType) {
          setPanelType(null);
        }
      },
      isActive: false,
    });
    breadcrumbItems.push({
      label: "Settings",
      icon: Settings,
      onClick: () => {},
      isActive: true,
    });
  }
  else if (isFunctionalOntology) {
    breadcrumbItems.push({
      label: "Dashboard",
      icon: BarChart3,
      onClick: () => {
        navigate(`/dashboard/${id}`);
      },
      isActive: false,
    });
    breadcrumbItems.push({
      label: "Functional Ontology from UI",
      icon: Palette,
      onClick: () => {},
      isActive: true,
    });
  }
const handleMenuAction = async (action: string) => {
    switch (action) {
      case "new-diagram":
        if (diagram && history) {
          diagram.setDiagramComponents([]);
          history.clearHistory();
        }
        break;
      case "load-diagram":
        // Open file picker for loading diagram
        if (fileInputRef.current) {
          fileInputRef.current.click();
        }
        break;
      case "save-diagram":
        openSaveDiagramDialog();
        break;
      case "save-component":
        openSaveDialog();
        break;
      case "download-svg":
        exportAsSVG();
        break;
      case "download-png":
        exportAsPNG();
        // TODO: Implement PNG download
        break;
      case "download-diagram":
        setIsDownloadDiagramDialogOpen(true);
        break;
      case "display-settings":
        openSettingsDialog();
        break;

      default:
    }

    setIsDropdownOpen(false);
  };

  const handleSaveComponent = async (
    name: string,
    description: string,
    category: string,
  ) => {
    // Validate name input
    if (!name || !name.trim()) {
      // TODO: Show error toast/notification
      return;
    }

    try {
      await saveComponent(name.trim(), description.trim(), category);
    } catch (error) {
      // Error handling is already done in the hook
    }
  };

  const handleSaveDiagram = (name: string, description: string) => {
    // Validate name input
    if (!name || !name.trim()) {
      // TODO: Show error toast/notification
      return;
    }

    if (
      !diagram ||
      !diagram.diagramComponents ||
      diagram.diagramComponents.length === 0
    ) {
      return;
    }

    // Generate properly formatted SVG content with bounding box calculation
    const svgContent = generateDiagramSVGContent(
      diagram.composedSVG,
      diagram.canvasSize,
    );
    saveDiagramMutation.mutate({
      name: name.trim(),
      uuid: id,
      description: description.trim(),
      diagramComponents: diagram.diagramComponents,
      svgContent,
    });
  };

  const handleCloseSaveDiagramDialog = () => {
    closeSaveDiagramDialog();
    setSaveDiagramError(null);
  };

  const handleClearSaveDiagramError = () => {
    setSaveDiagramError(null);
  };

  const handleDownloadDiagram = async (fileName: string) => {
    if (
      !diagram ||
      !diagram.diagramComponents ||
      diagram.diagramComponents.length === 0
    ) {
      toast.warning("Nothing to download — the diagram is empty.");
      return;
    }

    try {
      const jsonFileName = getJsonFileName(fileName);
      const serializedDiagramComponents =
        diagramComponentsLib.serializeDiagramComponents(
          diagram.diagramComponents,
        );
      const serializedComponentLib =
        componentLibraryManager.serializeComponentLib();

      const diagramData = {
        serializedDiagramComponents,
        serializedComponentLib,
      };

      await saveLocalFile(jsonFileName, JSON.stringify(diagramData, null, 2));
      toast.success(`Downloaded ${jsonFileName}`);
    } catch (error: any) {
      toast.error(`Failed to download diagram: ${error?.message || "unknown error"}`);
    }
  };

  const handleLoadDiagram = async (file?: File) => {
    try {
      if (!file) {
        throw new Error("No file selected for loading");
      }

      if (!diagram) {
        return;
      }

      const loadedData = await loadFile(file);
      const parsedData = JSON.parse(loadedData);
      // Deserialize the loaded data to retrieve diagram components and library information
      const deserializedComponents =
        diagramComponentsLib.deserializeDiagramComponents(
          parsedData.serializedDiagramComponents,
        );

      if (parsedData.serializedComponentLib) {
        componentLibraryManager.deserializeComponentLib(
          parsedData.serializedComponentLib,
        );
      }

      // Now call handleMissingDependencies with the deserialized components (matching diagram-tab pattern)
      if (handleMissingDependencies) {
        await handleMissingDependencies(deserializedComponents);
      }

      // Update the SVG library to include all loaded shapes
      if (setSvgLibrary) {
        setSvgLibrary(shapesLibraryManager.getAllShapes());
      }
      if (setComponents) {
        setComponents(componentLibraryManager.getAllComponents());
      }

      diagram.setDiagramComponents(deserializedComponents);
      diagram.selectShape(null);
      toast.success(`Loaded ${file.name}`);
    } catch (error: any) {
      toast.error(`Failed to load diagram: ${error?.message || "invalid file"}`);
    }
  };

  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      handleLoadDiagram(file);
    }
    // Reset the input value so the same file can be selected again
    event.target.value = "";
  };

  return (
    <header className="fixed left-20 top-0 h-10 flex items-center z-30 bg-background/95 backdrop-blur-sm">
      <nav className="flex items-center px-4">
        {/* Top row: Menu button - only show on pages that support diagram features */}
        {showDiagramMenu && (
          <div className="flex justify-start">
            <DropdownMenu
              open={isDropdownOpen}
              onOpenChange={setIsDropdownOpen}
            >
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  aria-label="Diagram menu"
                  className="p-1.5 gap-1 bg-surface border-surface-border hover:bg-surface-hover text-text-primary flex-shrink-0 w-fit"
                >
                  <MenuIcon className="text-text-primary w-4 h-4" />
                  <ChevronDown className="text-text-primary w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="w-[200px] bg-surface border-surface-border"
              >
                {MENU_ITEMS.map((menuGroup, groupIndex) => (
                  <div key={menuGroup.group}>
                    <DropdownMenuGroup>
                      {menuGroup.items.map((item) => {
                        const isDisabled =
                          (item.action === "save-component" ||
                            item.action === "save-diagram") &&
                          diagram &&
                          diagram.diagramComponents.length === 0;

                        return (
                          <DropdownMenuItem
                            key={item.action}
                            className={cn(
                              "text-text-primary hover:bg-surface-hover focus:bg-surface-hover cursor-pointer",
                              isDisabled &&
                                "opacity-50 cursor-not-allowed hover:bg-surface",
                            )}
                            onClick={() =>
                              !isDisabled && handleMenuAction(item.action)
                            }
                          >
                            {item.label}
                          </DropdownMenuItem>
                        );
                      })}
                    </DropdownMenuGroup>
                    {groupIndex < MENU_ITEMS.length - 1 && (
                      <DropdownMenuSeparator className="bg-surface-border" />
                    )}
                  </div>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
        {breadcrumbItems.map((item, index) => (
          <div key={index} className="flex items-center">
            {index > 0 && (
              <ChevronRight className="w-4 h-4 text-muted mx-1 flex-shrink-0" />
            )}
            <button
              onClick={item.onClick}
              disabled={item.isActive}
              className={cn(
                "flex items-center space-x-1.5 px-2 py-1 rounded-lg text-sm font-medium transition-all duration-200 group",
                item.isActive
                  ? "text-text-primary bg-surface-card cursor-default"
                  : "text-muted hover:text-text-primary hover:bg-surface-hover",
              )}
            >
              <item.icon
                className={cn(
                  "w-4 h-4 transition-colors duration-200",
                  item.isActive
                    ? "text-primary"
                    : "text-muted group-hover:text-primary",
                )}
              />
              <span className="truncate max-w-[200px]">{item.label}</span>
            </button>
          </div>
        ))}
      </nav>

      <SaveComponentDialog
        isOpen={isSaveDialogOpen}
        onClose={closeSaveDialog}
        onSave={handleSaveComponent}
      />

      <SaveNewDiagram
        mode="save"
        isOpen={isSaveDiagramDialogOpen}
        isPending={saveDiagramMutation.isPending}
        onClose={handleCloseSaveDiagramDialog}
        onSubmit={handleSaveDiagram}
        diagramInfo={diagram?.currentDiagramInfo || null}
        error={saveDiagramError}
        onErrorClear={handleClearSaveDiagramError}
      />

      <DownloadDiagramDialog
        isOpen={isDownloadDiagramDialogOpen}
        onClose={() => setIsDownloadDiagramDialogOpen(false)}
        onDownload={handleDownloadDiagram}
      />

      <SettingsDialog
        isOpen={isSettingsDialogOpen}
        onClose={closeSettingsDialog}
      />

      {/* Hidden file input for loading diagrams */}
      <input
        type="file"
        ref={fileInputRef}
        accept=".json"
        onChange={handleFileInputChange}
        style={{ display: "none" }}
      />
    </header>
  );
}
